import { type FC, useContext, useEffect, useState } from 'react';
import { type LevelUpSocketEventType } from '@common/types/socket';
import { addToBalance, addToStorage } from '@redux/reducers/userReducer';
import { useAppDispatch, useAppSelector } from '@redux/store';
import { logEvent } from 'firebase/analytics';
import { useTimeout } from 'usehooks-ts';

import { useLazyGetContractsQuery } from '@/api/queries/contract';
import { toStorageBalanceFormat } from '@/common/lib/storage/index';
import { analytics } from '@/firebase';

import { ModalContext } from '../../Modal/config';

import LevelUpClaimRewardScreen from './ui/LevelUpClaimRewardScreen';

import css from './styles.module.scss';

const LevelUpNotificationModal: FC<LevelUpSocketEventType> = ({
  wallet,
  materials,
  lottery,
  upgrade,
}) => {
  const { openModal } = useContext(ModalContext);
  const dispatch = useAppDispatch();
  const empireTotalCost = useAppSelector(
    (state) => state.user.extendedInfo?.totalEmpireValue,
  );
  const [isClaimed, setIsClaimed] = useState(false);
  const [productId, setProductId] = useState<string | undefined>();
  const [getContracts, { isLoading }] = useLazyGetContractsQuery();

  useTimeout(
    () => {
      if (productId) {
        openModal({
          renderKind: 'productOfDayModal',
          props: { productId },
        });
      }
    },
    productId && upgrade === 2 ? 2000 : null,
  );

  const handleClaim = (): void => {
    dispatch(addToBalance({ cash: wallet.cash, tgem: wallet.tgem }));

    if (materials) {
      dispatch(addToStorage(toStorageBalanceFormat(materials)));
    }

    if (upgrade === 2) {
      getContracts()
        .unwrap()
        .then((response) => {
          setProductId(response.productOfDay[0].id);
          setIsClaimed(true);
        });
    } else {
      setIsClaimed(true);
    }
  };

  useEffect(() => {
    if (!analytics) return;
    logEvent(analytics, 'level_up', {
      level: upgrade,
      empire_value: empireTotalCost,
    });
    logEvent(analytics, 'currency_earned', {
      currency_type: 'cash',
      amount: wallet.cash,
      source: 'level_up',
    });

    if (wallet.tgem) {
      logEvent(analytics, 'currency_earned', {
        currency_type: 'tgem',
        amount: wallet.tgem,
        source: 'level_up',
      });
    }
  }, []);

  return (
    <div className={css.wrapper}>
      <LevelUpClaimRewardScreen
        wallet={wallet}
        materials={materials}
        lottery={lottery}
        upgrade={upgrade}
        onClaim={handleClaim}
        isClaimed={isClaimed}
        isLoading={isLoading}
      />
    </div>
  );
};

export { LevelUpNotificationModal };
